import {
  MaterialReactTable,
  MRT_ActionMenuItem,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMaterialReactTable,
} from 'material-react-table';
import { FC, useMemo, useState } from 'react';
import { BarLoader } from 'react-spinners';
import UnlinkIcon from '@/assets/unlink.svg';
import ViewIcon from '@/assets/view.svg';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerProps } from '@/components/ui/drawer';
import { HeadingGroup } from '@/components/ui/heading-group';
import { Modal } from '@/components/ui/modal';
import { StatusBadge } from '@/components/ui/status-badge';

import { strings } from '@/constants/strings';
import { useCloseInvalidDrawer } from '@/hooks/use-close-invalid-drawer';
import { useDevicesInCurrentOrganisation } from '@/hooks/use-devices';
import { useOpenState } from '@/hooks/use-open-state';
import { usePendingState } from '@/hooks/use-pending-state';
import { useUnits } from '@/hooks/use-units';
import { useUser } from '@/hooks/use-user';
import { HeadingGroupContainerLayout } from '@/layouts/heading-group-container-layout';
import { ViewDevice } from '@/pages/authenticated/groups/group/unit/drawer-views/view-device';

import {
  useAddDeviceToUnitMutation,
  useRemoveDeviceFromUnitMutation,
} from '@/redux/api/endpoints/device';
import {
  composeTableOptions,
  getEntityRowId,
  getFilteredSelectedRows,
} from '@/table';
import { MaybeAssociatedDevice } from '@/types/device';
import { cx } from '@/utils/cx';
import {
  getDeviceStatusDisplay,
  getDeviceTypeLabel,
} from '@/utils/device-helpers';
import { isDeviceAssociated } from '@/utils/is-device-associated';
import { AssociateDevicesForm } from './drawer-views/associate-devices-form';
import { SelectedDevicesActionBar } from './selected-devices-action-bar';

type DevicesContext = { devices: MaybeAssociatedDevice[] };

type DrawerContext =
  | ({ view: 'link' } & DevicesContext)
  | { view: 'view'; deviceId: number | null };

export const OrganisationDevicesPage: FC = () => {
  const { data: allDevices, isFetching: isFetchingDevices, isLoading: isLoadingDevices } = useDevicesInCurrentOrganisation(); // prettier-ignore
  const { data: units } = useUnits();
  const { user } = useUser();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  const devices = useMemo(() => {
    const permittedGroups = user.permittedGroups;
    if (!permittedGroups || permittedGroups.length === 0) return allDevices;

    const permittedUnitIds = new Set(units.map((unit) => unit.id));
    return allDevices.filter(
      (device) => device.unit && permittedUnitIds.has(device.unit.id),
    );
  }, [allDevices, units, user.permittedGroups]);

  const columns: MRT_ColumnDef<MaybeAssociatedDevice>[] = [
    {
      id: 'serialNumber',
      accessorFn: ({ meta }) => meta?.serialNumber,
      filterVariant: 'text',
      header: 'Device Serial Number (DSN)',
      size: 150,
    },
    {
      id: 'type',
      accessorFn: ({ type }) => getDeviceTypeLabel(type),
      header: 'Type',
      filterVariant: 'multi-select',
      size: 120,
    },
    {
      id: 'model',
      accessorFn: ({ meta }) => meta?.model,
      header: 'Model',
      filterVariant: 'multi-select',
      size: 150,
      Cell: ({ renderedCellValue }) => renderedCellValue || '-',
    },
    {
      id: 'uni',
      accessorFn: ({ unit }) => unit?.unitName,
      header: 'Associated Unit',
      size: 150,
      Cell: ({ renderedCellValue, row }) => {
        const isAssociated = Boolean(row.original.unit);
        return (
          <div className={cx(!isAssociated && 'italic')}>
            {isAssociated ? renderedCellValue : 'Non-associated'}
          </div>
        );
      },
    },
    {
      id: 'status',
      filterVariant: 'multi-select',
      accessorFn: (device) => getDeviceStatusDisplay(device).text,
      header: 'Status',
      grow: false,
      size: 100,
      Cell: ({ row }) => {
        const { text, variant } = getDeviceStatusDisplay(row.original);
        return isFetchingDevices ? (
          <BarLoader color={'gray'} />
        ) : (
          <StatusBadge text={text} variant={variant} />
        );
      },
    },
  ];

  // Drawer states for viewing a device, associating devices, and sending announcements
  const { state: drawerState, open: openDrawer, toggleOpen: toggleDrawerOpen } = useOpenState<DrawerContext>({ view: 'view', deviceId: null }); // prettier-ignore
  const { state: unassociateDeviceConfirmationState, open: openUnassociateDeviceConfirmation, toggleOpen: toggleUnassociateDeviceConfirmationOpen } = useOpenState<DevicesContext>({ devices: [] }); // prettier-ignore

  const {
    pending: removeDeviceFromUnitPending,
    setPending: setRemoveDeviceFromUnitPending,
  } = usePendingState();

  const [addDeviceToUnit] = useAddDeviceToUnitMutation();
  const [removeDeviceFromUnit] = useRemoveDeviceFromUnitMutation();

  const table = useMaterialReactTable({
    ...composeTableOptions<MaybeAssociatedDevice>({
      enableRowActions: true,
      enableRowSelection: true,
    }),
    columns,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    enableStickyHeader: true,
    data: devices,
    getRowId: getEntityRowId,
    state: {
      isLoading: isLoadingDevices,
      showProgressBars: isFetchingDevices,
      showColumnFilters: true,
      rowSelection,
      ...(isLoadingDevices
        ? {
            pagination: {
              pageIndex: 0,
              pageSize: 1,
            },
          }
        : {}),
    },
    onRowSelectionChange: setRowSelection,
    renderRowActionMenuItems: ({ row, table, closeMenu }) => [
      <MRT_ActionMenuItem
        icon={<ViewIcon />}
        key="view"
        label="View"
        onClick={() => {
          closeMenu();
          openDrawer({ view: 'view', deviceId: row.original.id });
        }}
        table={table}
      />,
      row.original.unit ? (
        <MRT_ActionMenuItem
          icon={<UnlinkIcon />}
          key="unlink"
          label="Unassociate"
          onClick={() => {
            closeMenu();
            openUnassociateDeviceConfirmation({ devices: [row.original] });
          }}
          table={table}
        />
      ) : null,
    ],
  });

  const selectedDevices = getFilteredSelectedRows(table);

  const viewedDeviceId =
    drawerState.context.view === 'view' ? drawerState.context.deviceId : null;
  const viewedDevice = devices.find((device) => device.id === viewedDeviceId);

  useCloseInvalidDrawer(
    drawerState.open,
    drawerState.context.view === 'view'
      ? Boolean(viewedDevice)
      : drawerState.context.devices.length > 0,
    toggleDrawerOpen,
  );

  const getDrawerTitleProps = (): Pick<DrawerProps, 'title' | 'subtitle'> => {
    switch (drawerState.context.view) {
      case 'link':
        return {
          title: strings.devices.drawer_associate.title,
          subtitle: strings.devices.drawer_associate.subtitle,
        };
      case 'view':
        return {
          title: strings.devices.drawer_view.title,
        };
      default:
        return {
          title: '',
        };
    }
  };

  /**
   * The side drawer content is determined by the current view state. It can be
   * either to link devices to a unit (associate, unassociate) or to view
   * details of a device.
   */
  const renderDrawerContent = () => {
    switch (drawerState.context.view) {
      case 'link': {
        const { devices } = drawerState.context;
        if (devices && devices.length > 0) {
          return (
            <AssociateDevicesForm
              devices={devices}
              onCancel={() => toggleDrawerOpen(false)}
              onSubmit={async (data) => {
                const unitId = data.unitId;
                await Promise.all(
                  devices.map((device) =>
                    addDeviceToUnit({
                      device,
                      unitId,
                    }),
                  ),
                );
                toggleDrawerOpen(false);
              }}
              units={units}
            />
          );
        }
        break;
      }
      case 'view': {
        const device = viewedDevice;
        if (device) {
          return (
            <ViewDevice
              device={device}
              key={device.id}
              onClose={() => toggleDrawerOpen(false)}
              onUnlink={() => {
                openUnassociateDeviceConfirmation({ devices: [device] });
              }}
              unlinkType="unit"
            />
          );
        }
        break;
      }
      default:
        break;
    }

    return null;
  };

  const hasAssociatedSelectedDevice = selectedDevices.some(
    (device) => !!device.unit,
  );

  return (
    <>
      <HeadingGroupContainerLayout>
        <HeadingGroup
          subtitlePostfix={strings.devices.subtitlePostfix}
          subtitlePrefix={strings.devices.subtitlePrefix}
          subtitleTooltip={strings.devices.subtitleTooltip}
          title={strings.devices.title}
          tooltip={strings.devices.organisation.tooltip}
        />
      </HeadingGroupContainerLayout>
      <SelectedDevicesActionBar
        linkDeviceButtonText="Associate device"
        onLinkDevicesClick={() =>
          openDrawer({ view: 'link', devices: selectedDevices })
        }
        onUnlinkDevicesClick={() =>
          openUnassociateDeviceConfirmation({ devices: selectedDevices })
        }
        selectedDevices={selectedDevices}
        unlinkDeviceButtonText="Unassociate device"
        unlinkDisabled={!hasAssociatedSelectedDevice}
      />
      <MaterialReactTable table={table} />
      <Drawer
        {...getDrawerTitleProps()}
        open={drawerState.open}
        setOpen={toggleDrawerOpen}
      >
        {renderDrawerContent()}
      </Drawer>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => toggleUnassociateDeviceConfirmationOpen(false)}
            variant="secondary"
          >
            {strings.devices.modal_confirm_unassociate.cancel}
          </Button>,
          <Button
            disabled={removeDeviceFromUnitPending}
            key="delete"
            onClick={async () => {
              setRemoveDeviceFromUnitPending(true);
              await Promise.all(
                unassociateDeviceConfirmationState.context.devices
                  .filter(isDeviceAssociated)
                  .map((device) =>
                    removeDeviceFromUnit({
                      device,
                    }),
                  ),
              );
              setRemoveDeviceFromUnitPending(false);
              toggleUnassociateDeviceConfirmationOpen(false);
            }}
            variant="primary-warning"
          >
            {strings.devices.modal_confirm_unassociate.unassociate}
          </Button>,
        ]}
        description={strings.devices.modal_confirm_unassociate.description}
        loading={removeDeviceFromUnitPending}
        open={unassociateDeviceConfirmationState.open}
        setOpen={toggleUnassociateDeviceConfirmationOpen}
        title={strings.devices.modal_confirm_unassociate.title}
      />
    </>
  );
};
